import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { CheckInComponent } from '../../blocks/email-blocks/check-in/check-in.component';
import { ConciergeComponent } from '../../blocks/email-blocks/concierge/concierge.component';
import { CounterComponent } from '../../blocks/email-blocks/counter/counter.component';
import { DisclaimerComponent } from '../../blocks/email-blocks/disclaimer/disclaimer.component';
import { SelectModule } from '@openng/optimus-ui/select';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';
import { EventComponent } from '../../blocks/email-blocks/event/event.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GiftVoucherComponent } from '../../blocks/email-blocks/gift-voucher/gift-voucher.component';
import { HeadingComponent } from '../../blocks/email-blocks/heading/heading.component';
import { InspirationComponent } from '../../blocks/email-blocks/inspiration/inspiration.component';
import { MainBannerComponent } from '../../blocks/email-blocks/main-banner/main-banner.component';
import { MomentsComponent } from '../../blocks/email-blocks/moments/moments.component';
import { NgForOf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { Only2PhotosComponent } from '../../blocks/email-blocks/only-2-photos/only-2-photos.component';
import { OnlyCtaComponent } from '../../blocks/email-blocks/only-cta/only-cta.component';
import { OnlyIntroComponent } from '../../blocks/email-blocks/only-intro/only-intro.component';
import { ParagraphCtaComponent } from '../../blocks/email-blocks/paragraph-cta/paragraph-cta.component';
import {
  PropertyDescriptionComponent
} from '../../blocks/email-blocks/property-description/property-description.component';
import {
  PropertyUsp1xPhotoComponent
} from '../../blocks/email-blocks/property-usp-1x-photo/property-usp-1x-photo.component';
import {
  PropertyUsp2xPhotoComponent
} from '../../blocks/email-blocks/property-usp-2x-photo/property-usp-2x-photo.component';
import { ReservationComponent } from '../../blocks/email-blocks/reservation/reservation.component';
import { ReviewsComponent } from '../../blocks/reviews/reviews.component';
import { ValamarAppComponent } from '../../blocks/email-blocks/valamar-app/valamar-app.component';
import { VoucherComponent } from '../../blocks/email-blocks/voucher/voucher.component';
import { X2ExperiencesComponent } from '../../blocks/email-blocks/x2-experiences/x2-experiences.component';
import { X2PropRecComponent } from '../../blocks/email-blocks/x2-prop-rec/x2-prop-rec.component';
import { X2PropRecOnpComponent } from '../../blocks/email-blocks/x2-prop-rec-onp/x2-prop-rec-onp.component';
import { X3ExperiencesComponent } from '../../blocks/email-blocks/x3-experiences/x3-experiences.component';
import {
  ZigZagHeadingAndAltCtaComponent
} from '../../blocks/email-blocks/zigzag-heading-and-alt-cta/zigzag-heading-and-alt-cta';
import { ZigZagHeadingAndCtaComponent } from '../../blocks/email-blocks/zigzag-heading-and-cta/zigzag-heading-and-cta';
import {
  ZigZagNoHeadingNoButtonComponent
} from '../../blocks/email-blocks/zigzag-no-heading-no-button/zigzag-no-heading-no-button';

import { BaseModel } from '../../../../models/email-template/base-model.interface';
import { SmsComponent } from '../../blocks/sms-blocks/sms.component';
import { PushComponent } from '../../blocks/push-blocks/push.component';
import { WebPushComponent } from '../../blocks/web-push-blocks/web-push.component';
import { ViberComponent } from '../../blocks/viber-blocks/viber.component';
import { RcsComponent } from '../../blocks/rcs-blocks/rcs.component';
import { WhatsappComponent } from '../../blocks/whatsapp-blocks/whatsapp.component';
import { TranslateService } from '../../../../services/translate.service';
import { TemplateService } from '../../../../services/template.service';
import { TypeEnum } from '../../../../models/template/type.enum';

export interface TemplateLanguage {
  language: string;
  languageCode: string;
  blocks: BaseModel[];
  translated?: boolean;
}

@Component({
    selector: 'app-preview',
    imports: [
        CommonModule,
        CheckInComponent,
        ConciergeComponent,
        CounterComponent,
        DisclaimerComponent,
        SelectModule,
        ProgressSpinnerModule,
        EventComponent,
        FormsModule,
        GiftVoucherComponent,
        HeadingComponent,
        InspirationComponent,
        MainBannerComponent,
        MomentsComponent,
        NgForOf,
        NgSwitchCase,
        Only2PhotosComponent,
        OnlyCtaComponent,
        OnlyIntroComponent,
        ParagraphCtaComponent,
        PropertyDescriptionComponent,
        PropertyUsp1xPhotoComponent,
        PropertyUsp2xPhotoComponent,
        ReactiveFormsModule,
        ReservationComponent,
        ReviewsComponent,
        ValamarAppComponent,
        VoucherComponent,
        X2ExperiencesComponent,
        X2PropRecComponent,
        X2PropRecOnpComponent,
        X3ExperiencesComponent,
        ZigZagHeadingAndAltCtaComponent,
        ZigZagHeadingAndCtaComponent,
        ZigZagNoHeadingNoButtonComponent,
        SmsComponent,
        NgSwitch,
        NgSwitchDefault,
        PushComponent,
        WebPushComponent,
        ViberComponent,
        RcsComponent,
        WhatsappComponent
    ],
    templateUrl: './preview.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './preview.component.scss'
})
export class PreviewComponent implements OnInit, OnDestroy {
  selectedBlocks: BaseModel[] = [];
  templateName: string = "";
  idcustom: string = "";
  templateBasePath: string = '';

  // Language slider properties
  availableLanguages: TemplateLanguage[] = [];
  currentLanguageIndex: number = 0;
  isLoadingLanguages: boolean = true;

  // All supported languages to try
  private readonly SUPPORTED_LANGUAGES = [
    {code: 'en', name: 'English'},
    {code: 'de', name: 'Deutsch'},
    {code: 'it', name: 'Italiano'},
    {code: 'hr', name: 'Hrvatski'},
    {code: 'sl', name: 'Slovenščina'},
    {code: 'cs', name: 'Čeština'},
    {code: 'pl', name: 'Polski'},
    {code: 'ru', name: 'Русский'},
    {code: 'hu', name: 'Magyar'},
    {code: 'sk', name: 'Slovenčina'},
    {code: 'fr', name: 'Français'},
    {code: 'es', name: 'Español'}
  ];

  // Touch gesture properties
  private touchStartX: number = 0;
  private touchEndX: number = 0;
  private readonly SWIPE_THRESHOLD: number = 50;

  private _destroy$ = new Subject<void>();
  private getHostUrl = () => {
    const url = environment.scheme + environment.host + environment.port + '/';
    return url;
  };

  constructor(
    private router: Router,
    private http: HttpClient,
    private translateService: TranslateService,
    private templateService: TemplateService
  ) {
    const stateData: {
      selectedBlocks: BaseModel[],
      templateName: string,
      idcustom: string,
      templateBasePath: string,
      language?: string
    } = history.state.data;
    if (stateData?.selectedBlocks) {
      stateData.selectedBlocks.forEach((block: BaseModel) => {
        block.notifications = []
      })
      this.selectedBlocks = stateData.selectedBlocks;
      this.templateName = stateData.templateName;
      this.idcustom = stateData.idcustom;
      this.templateBasePath = stateData.templateBasePath;
    } else {
      console.warn('No state data found in preview!');
    }
  }

  ngOnInit(): void {
    this.loadAvailableLanguages();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Loads all available languages for the template
   * Tries to load templates for all supported languages and keeps only those that exist
   */
  loadAvailableLanguages(): void {
    if (!this.templateName) {
      this.isLoadingLanguages = false;
      return;
    }

    const templateType = this.getTemplateTypeFromPath();
    const currentLang = this.getCurrentLanguageFromState();

    // Initialize with all supported languages (will be filtered later)
    this.availableLanguages = this.SUPPORTED_LANGUAGES.map(lang => ({
      language: lang.name,
      languageCode: lang.code,
      blocks: [],
      translated: false
    }));

    let loadedCount = 0;
    const validLanguageIndices: number[] = [];

    // Try to load template for each language
    this.availableLanguages.forEach((lang, index) => {
      this.templateService.getTemplate({
        templateName: this.templateName,
        templateType: templateType,
        language: lang.languageCode
      }).pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (res: any) => {
            console.log(`Checked language ${lang.languageCode}:`, res?.blocks?.length || 0, 'blocks');
            if (res && res.blocks && res.blocks.length > 0) {
              this.availableLanguages[index].blocks = res.blocks;
              this.availableLanguages[index].translated = true;
              validLanguageIndices.push(index);
            }
            loadedCount++;
            console.log(`Progress: ${loadedCount}/${this.availableLanguages.length}`);

            if (loadedCount === this.availableLanguages.length) {
              this.onAllLanguagesChecked(validLanguageIndices, currentLang);
            }
          },
          error: (err) => {
            console.error(`Language ${lang.languageCode} not found (status ${err.status})`);
            loadedCount++;
            if (loadedCount === this.availableLanguages.length) {
              this.onAllLanguagesChecked(validLanguageIndices, currentLang);
            }
          }
        });
    });
  }

  /**
   * Called after checking all languages
   * Filters to only languages that have templates and sets current index
   */
  onAllLanguagesChecked(validIndices: number[], currentLang: string): void {
    if (validIndices.length > 0) {
      // Filter to only languages with valid templates
      this.availableLanguages = validIndices.map(i => this.availableLanguages[i]);
      console.log('Filtered to', this.availableLanguages.length, 'languages with templates');

      // Set current language index
      const currentIndex = this.availableLanguages.findIndex(
        lang => lang.languageCode === currentLang
      );
      this.currentLanguageIndex = currentIndex >= 0 ? currentIndex : 0;
      console.log('Setting currentLanguageIndex to:', this.currentLanguageIndex);
    } else {
      // No templates found - use fallback
      console.warn('No templates found for any language, using selectedBlocks as fallback');
      this.availableLanguages = [{
        language: this.getLanguageName(currentLang),
        languageCode: currentLang,
        blocks: this.selectedBlocks,
        translated: true
      }];
      this.currentLanguageIndex = 0;
    }

    this.isLoadingLanguages = false;
    console.log('isLoadingLanguages = false, final availableLanguages.length =', this.availableLanguages.length);
  }

  /**
   * Loads template content for a specific language by index
   * Note: Blocks are now loaded upfront in loadAvailableLanguages, so this just sets loading state
   * @param index - The index of the language in availableLanguages array
   */
  loadLanguageTemplate(index: number): void {
    // Blocks are already loaded in loadAvailableLanguages
    // Just clear loading state since data is ready
    setTimeout(() => {
      this.isLoadingLanguages = false;
    }, 100);
  }

  /**
   * Gets the template type from the base path
   */
  getTemplateTypeFromPath(): TypeEnum {
    if (this.templateBasePath.includes('email')) return TypeEnum.EMAIL;
    if (this.templateBasePath.includes('sms')) return TypeEnum.SMS;
    if (this.templateBasePath.includes('web-push')) return TypeEnum.WEBPUSH;
    if (this.templateBasePath.includes('push')) return TypeEnum.PUSH;
    if (this.templateBasePath.includes('whatsapp')) return TypeEnum.WHATSAPP;
    if (this.templateBasePath.includes('viber')) return TypeEnum.VIBER;
    if (this.templateBasePath.includes('rcs')) return TypeEnum.RCS;
    return TypeEnum.EMAIL;
  }

  /**
   * Gets the current language from state or route
   */
  getCurrentLanguageFromState(): string {
    const stateData: any = history.state.data;
    if (stateData?.language) {
      return stateData.language;
    }
    // Try to extract from templateBasePath or use default
    console.log('No language in state, using default: en');
    return 'en';
  }

  /**
   * Gets language display name from code
   */
  getLanguageName(code: string): string {
    const languageNames: { [key: string]: string } = {
      'en': 'English',
      'de': 'Deutsch',
      'it': 'Italiano',
      'hr': 'Hrvatski',
      'sl': 'Slovenščina',
      'cs': 'Čeština',
      'pl': 'Polski',
      'ru': 'Русский',
      'hu': 'Magyar',
      'sk': 'Slovenčina',
      'fr': 'Français',
      'es': 'Español'
    };
    return languageNames[code] || code.toUpperCase();
  }

  /**
   * Swipe handler for left/right navigation
   */
  onSwipe(direction: string): void {
    if (this.isLoadingLanguages || this.availableLanguages.length <= 1) {
      return;
    }

    if (direction === 'left') {
      this.nextLanguage();
    } else if (direction === 'right') {
      this.previousLanguage();
    }
  }

  /**
   * Navigates to the next language
   */
  nextLanguage(): void {
    if (this.currentLanguageIndex < this.availableLanguages.length - 1) {
      this.currentLanguageIndex++;
      this.isLoadingLanguages = true;
      this.loadLanguageTemplate(this.currentLanguageIndex);
    }
  }

  /**
   * Navigates to the previous language
   */
  previousLanguage(): void {
    if (this.currentLanguageIndex > 0) {
      this.currentLanguageIndex--;
      this.isLoadingLanguages = true;
      this.loadLanguageTemplate(this.currentLanguageIndex);
    }
  }

  /**
   * Checks if can go to next language
   */
  get canGoNext(): boolean {
    return this.currentLanguageIndex < this.availableLanguages.length - 1;
  }

  /**
   * Checks if can go to previous language
   */
  get canGoPrevious(): boolean {
    return this.currentLanguageIndex > 0;
  }

  /**
   * Gets the current language blocks
   */
  get currentBlocks(): BaseModel[] {
    // Log for debugging
    console.log('currentBlocks called', {
      hasSelectedBlocks: this.selectedBlocks?.length > 0,
      selectedBlocksCount: this.selectedBlocks?.length || 0,
      availableLanguagesCount: this.availableLanguages?.length || 0,
      currentIndex: this.currentLanguageIndex,
      isLoading: this.isLoadingLanguages
    });

    if (this.availableLanguages.length > 0 && this.availableLanguages[this.currentLanguageIndex]) {
      const blocks = this.availableLanguages[this.currentLanguageIndex].blocks;
      if (blocks && blocks.length > 0) {
        console.log('Returning language blocks:', blocks.length);
        return blocks;
      }
    }

    // Fallback to selectedBlocks
    console.log('Fallback to selectedBlocks:', this.selectedBlocks?.length || 0);
    return this.selectedBlocks || [];
  }

  /**
   * Gets the current language display info
   */
  get currentLanguageDisplay(): string {
    if (this.availableLanguages.length > 0 && this.availableLanguages[this.currentLanguageIndex]) {
      return this.availableLanguages[this.currentLanguageIndex].language;
    }
    return '';
  }

  goBack(): void {
    this.router.navigate([`${this.templateBasePath}${this.templateName}`], {
      state: {
        data: {
          "selectedBlocks": this.selectedBlocks,
          "templateName": this.templateName,
          "idcustom": this.idcustom
        }
      }
    });
  }

  /**
   * Touch event handlers for swipe gesture
   */
  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  onTouchMove(event: TouchEvent): void {
    // Prevent scrolling while swiping horizontally
    const touchX = event.changedTouches[0].screenX;
    const diff = Math.abs(touchX - this.touchStartX);
    if (diff > 10) {
      event.preventDefault();
    }
  }

  onTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipeGesture();
  }

  /**
   * Handles swipe gesture based on touch start/end positions
   */
  handleSwipeGesture(): void {
    const diff = this.touchEndX - this.touchStartX;

    if (Math.abs(diff) > this.SWIPE_THRESHOLD) {
      if (diff > 0) {
        // Swiped right - go to previous language
        this.onSwipe('right');
      } else {
        // Swiped left - go to next language
        this.onSwipe('left');
      }
    }
  }

  /**
   * Mouse event handlers for desktop swipe support (drag to navigate)
   */
  onMouseDown(event: MouseEvent): void {
    this.touchStartX = event.screenX;
  }

  onMouseUp(event: MouseEvent): void {
    this.touchEndX = event.screenX;
    this.handleSwipeGesture();
  }
}
